import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'

export const EXPENSE_ATTACHMENT_KINDS = [
  'INVOICE',
  'RECEIPT',
  'BILL',
  'PAYMENT_VOUCHER',
  'OTHER',
] as const
export type ExpenseAttachmentKind = (typeof EXPENSE_ATTACHMENT_KINDS)[number]

export const EXPENSE_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024

export const EXPENSE_ATTACHMENT_ACCEPT = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  '.docx',
  '.xlsx',
].join(',')

const attachmentSchema = z.object({
  id: uuidSchema,
  kind: z.enum(EXPENSE_ATTACHMENT_KINDS),
  originalName: z.string().nullish(),
  contentType: z.string(),
  sizeBytes: z.number(),
  createdBy: uuidSchema,
  createdAt: instantSchema,
})

export interface ExpenseAttachment {
  id: string
  kind: ExpenseAttachmentKind
  originalName: string | null
  contentType: string
  sizeBytes: number
  createdBy: string
  createdAt: string
}

function toAttachment(raw: z.infer<typeof attachmentSchema>): ExpenseAttachment {
  return { ...raw, originalName: raw.originalName ?? null }
}

const base = (entryId: string) => `/api/finance/expenses/${entryId}/attachments`

export async function fetchExpenseAttachments(
  entryId: string,
  signal?: AbortSignal,
): Promise<ExpenseAttachment[]> {
  const response = await api.get<unknown>(base(entryId), { ...(signal ? { signal } : {}) })
  return z.array(attachmentSchema).parse(response.data).map(toAttachment)
}

export async function uploadExpenseAttachment(
  entryId: string,
  kind: ExpenseAttachmentKind,
  file: File,
): Promise<ExpenseAttachment> {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const response = await api.post<unknown>(base(entryId), form, {
    headers: { 'Content-Type': undefined },
  })
  return toAttachment(attachmentSchema.parse(response.data))
}

export async function expenseAttachmentUrl(entryId: string, attachmentId: string): Promise<string> {
  const response = await api.get<unknown>(`${base(entryId)}/${attachmentId}/url`)
  return z.object({ url: z.string() }).parse(response.data).url
}

export async function deleteExpenseAttachment(entryId: string, attachmentId: string): Promise<void> {
  await api.delete(`${base(entryId)}/${attachmentId}`)
}
